import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["active", "arrow", "autofill", "error", "fieldTooltip", "fieldTooltipDoNotFold", "hasFluidWidth", "ariaLabel", "disabled", "icon", "ids", "onBlur", "onClick", "onFocus", "UNSAFE_containerProps", "styleSheet", "warning"];
import React, { useRef } from 'react';
import { compose, defaultProps, setDisplayName, shouldUpdate, withProps } from 'recompose';
import omitProps from '@hocs/omit-props';
import { withFocusProps, withHoverProps, withUncontrolledProp } from '@klarna/higher-order-components';
import TouchableIcon from '@bubble-ui/molecule-touchable-icon';
import wrapOverridableComponent from '@bubble-ui/hoc-wrap-overridable-component';
import View from '@bubble-ui/primitive-view';
import withPropDeprecationWarning from '@bubble-ui/hoc-with-prop-deprecation-warning';
import { getIconType } from './utils';
import TooltipPortal from './TooltipPortal';

var getDefaultStyleSheet = function getDefaultStyleSheet(_ref) {
  var active = _ref.active,
      withPadding = _ref.withPadding,
      icon = _ref.icon;
  var iconSize = 20;
  var clickableRadius = 6;
  var iconClickableSize = iconSize + clickableRadius * 2;
  var paddingRight = withPadding ? 15 : 0;
  var fieldHeight = 60;
  return {
    rootStyle: {
      cursor: icon ? 'pointer' : 'auto',
      position: 'initial'
    },
    iconStyle: {
      alignSelf: 'center',
      paddingRight: paddingRight,
      position: 'absolute',
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)'
    },
    hoverElementStyle: {
      opacity: active ? 1 : 0,
      position: 'absolute',
      top: (fieldHeight - iconClickableSize) / 2,
      right: paddingRight - clickableRadius,
      height: iconClickableSize,
      width: iconClickableSize
    },
    tooltipStyle: {}
  };
};

var getIds = function getIds(_ref2) {
  var id = _ref2.id;
  return {
    ids: id ? {
      root: id,
      iconWrapper: "".concat(id, "__icon-wrapper"),
      icon: "".concat(id, "__icon"),
      span: "".concat(id, "__span"),
      tooltipWrapper: "".concat(id, "__tooltip-wrapper"),
      tooltip: "".concat(id, "__tooltip")
    } : {}
  };
};

function FieldTooltip(_ref3) {
  var active = _ref3.active,
      arrow = _ref3.arrow,
      autofill = _ref3.autofill,
      error = _ref3.error,
      fieldTooltip = _ref3.fieldTooltip,
      fieldTooltipDoNotFold = _ref3.fieldTooltipDoNotFold,
      hasFluidWidth = _ref3.hasFluidWidth,
      ariaLabel = _ref3.ariaLabel,
      disabled = _ref3.disabled,
      icon = _ref3.icon,
      ids = _ref3.ids,
      onBlur = _ref3.onBlur,
      onClick = _ref3.onClick,
      onFocus = _ref3.onFocus,
      UNSAFE_containerProps = _ref3.UNSAFE_containerProps,
      _ref3$styleSheet = _ref3.styleSheet,
      rootStyle = _ref3$styleSheet.rootStyle,
      iconStyle = _ref3$styleSheet.iconStyle,
      hoverElementStyle = _ref3$styleSheet.hoverElementStyle,
      tooltipStyle = _ref3$styleSheet.tooltipStyle,
      warning = _ref3.warning,
      props = _objectWithoutProperties(_ref3, _excluded);

  var parentRef = useRef(null);
  return /*#__PURE__*/React.createElement(View, _extends({}, UNSAFE_containerProps, {
    id: ids.root,
    onClick: onClick,
    style: rootStyle
  }, props), /*#__PURE__*/React.createElement(View, {
    id: ids.iconWrapper,
    style: iconStyle
  }, /*#__PURE__*/React.createElement(TouchableIcon, {
    Icon: getIconType({
      error: error,
      Icon: icon,
      warning: warning
    }),
    id: ids.icon,
    onFocus: onFocus,
    onBlur: onBlur,
    grey: !error && !warning && !disabled && !autofill,
    ariaLabel: ariaLabel
  })), /*#__PURE__*/React.createElement("span", {
    id: ids.span,
    style: hoverElementStyle,
    ref: parentRef
  }), active && /*#__PURE__*/React.createElement(View, {
    id: ids.tooltipWrapper,
    style: tooltipStyle
  }, /*#__PURE__*/React.createElement(TooltipPortal, _extends({
    parentRef: parentRef,
    hasFluidWidth: hasFluidWidth,
    arrow: arrow,
    id: ids.tooltip
  }, fieldTooltipDoNotFold === undefined ? {} : {
    isLong: !fieldTooltipDoNotFold
  }), fieldTooltip)));
}

export default compose(setDisplayName('Field.FieldTooltip'), defaultProps({
  active: false
}), withHoverProps({
  active: true
}), withFocusProps({
  active: true
}), withUncontrolledProp({
  prop: 'active',
  handlers: {
    onClick: function onClick(props) {
      return function () {
        return props.active = true;
      };
    }
  }
}), shouldUpdate(function (prevProps, nextProps) {
  if (prevProps.active !== nextProps.active) {
    nextProps.onActive(nextProps.active);
  }

  return prevProps.active !== nextProps.active || prevProps.warning !== nextProps.warning || prevProps.error !== nextProps.error;
}), wrapOverridableComponent('Field.FieldTooltip', getDefaultStyleSheet), withProps(getIds), omitProps('big', 'id', 'withPadding', 'onActive'), withPropDeprecationWarning({
  name: 'fieldTooltipDoNotFold',
  removalDate: 'v5.0.0'
}))(FieldTooltip);
//# sourceMappingURL=index.js.map